import { buildDataset, type ExportColumn, type ExportDataset, type ExportMetadata } from "./types";

/**
 * bso adapter: maps the user's Bible annotations (personal NOTES + verse
 * HIGHLIGHTS) into the generic {@link ExportDataset}. This is the only
 * bso-specific export piece — other clones swap it for their own entity.
 *
 * Notes and highlights don't share a SQLite table (`bible_note` +
 * `bible_note_verse` vs `bible_verse_highlight`) and the bible reference is a
 * derived string (verse coordinates → "John 3:16", localized), so — unlike the
 * canon todo adapter that consumes raw rows — this adapter takes rows already
 * NORMALIZED by the view (which owns `formatVerseReference` and i18n). The
 * export stays a single combined file so a study export carries both kinds; the
 * `kind` column distinguishes them (bso annotations have no per-item title
 * field, so it fills the leftmost "title" slot).
 */
export interface BibleAnnotationExportRow {
  /** Localized annotation kind label ("Note" / "Highlight"). */
  kind: string;
  /** Human-readable, localized bible reference (e.g. "John 3:16"). */
  reference: string;
  /** Free text: the note body; empty for highlights (their content is the verses). */
  text: string;
  /** ISO timestamp the annotation was created. */
  createdAt: string;
}

/**
 * English kind labels for the combined export. Kept here (not in the view) so
 * the export content stays English — matching the canon todo adapter's hardcoded
 * "Yes"/"No" — and so the view carries no unlocalized string literals.
 */
export const BIBLE_ANNOTATION_KIND = {
  note: "Note",
  highlight: "Highlight",
} as const;

/** Localized column headers, supplied by the view (keeps this module i18n-free). */
export interface BibleAnnotationHeaders {
  kind: string;
  reference: string;
  text: string;
  date: string;
}

const DEFAULT_HEADERS: BibleAnnotationHeaders = {
  kind: "Type",
  reference: "Reference",
  text: "Text",
  date: "Date",
};

export function bibleAnnotationColumns(
  headers: BibleAnnotationHeaders = DEFAULT_HEADERS,
): ExportColumn<BibleAnnotationExportRow>[] {
  return [
    { key: "kind", header: headers.kind, accessor: (r) => r.kind },
    { key: "reference", header: headers.reference, accessor: (r) => r.reference },
    { key: "text", header: headers.text, accessor: (r) => r.text },
    { key: "created_at", header: headers.date, accessor: (r) => r.createdAt },
  ];
}

/**
 * Builds an {@link ExportDataset} from normalized annotation rows, ready for
 * `datasetToCsv` / `datasetToHtml`.
 */
export function buildBibleAnnotationsDataset(
  rows: readonly BibleAnnotationExportRow[],
  metadata: ExportMetadata = {},
  headers?: BibleAnnotationHeaders,
): ExportDataset {
  return buildDataset(bibleAnnotationColumns(headers), rows, {
    title: "Bible annotations",
    ...metadata,
  });
}
